/**
 * CityHubPage — /cities/hubs/:tag  (2026-02-29)
 *
 * SEO landing page listing top student cities for a given sector or
 * fine industry (law, aerospace, fintech, biotech, …). Backed by
 * /api/cities/hubs/:tag. Designed for organic-search surface and
 * shareability — every entry has a rank, an evidence chip
 * (raw_max/10 + hubs list), and a direct link to the full city guide.
 */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/app/SEOHead";
import {
  Briefcase, MapPin, ArrowRight, GraduationCap,
  Sparkles, Trophy, ShieldCheck,
} from "lucide-react";
import { CreativeProvenanceChip } from "@/components/CreativeProvenanceChip";

// Tags where the Creative & Media score drives the ranking — chip is
// only meaningful for these hubs (broader hubs like Finance/Law use
// unrelated data sources).
const CREATIVE_HUB_TAGS = new Set([
  "creative", "publishing", "content_creation", "agencies",
]);

const SAFETY_COLOR = {
  "A+": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "A":  "bg-emerald-50 text-emerald-700 border-emerald-200",
  "B":  "bg-amber-50 text-amber-800 border-amber-200",
  "C":  "bg-orange-50 text-orange-800 border-orange-200",
  "D":  "bg-red-50 text-red-800 border-red-200",
};

const TAG_INTRO = {
  law:       "the world's densest concentrations of Magic Circle and top international law firms",
  finance:   "global financial centres by GFCI score and banking-sector density",
  tech:      "leading software-and-AI ecosystems by startup output and hiring density",
  fintech:   "cities running the world's payment, banking-tech and crypto companies",
  aerospace: "cities anchoring aerospace manufacturing and aviation R&D",
  biotech:   "cities with the deepest healthcare and life-sciences research pipelines",
  cleantech: "leaders in renewable energy, sustainability and climate-tech investment",
  gaming:    "cities driving global games, media and streaming production",
  ecommerce: "leading online retail, marketplace and DTC ecosystems",
  mobility:  "cities anchoring autonomous vehicles, logistics and mobility R&D",
  agritech:  "the world's food-and-agriculture innovation hubs",
  edtech:    "cities running the world's education-technology companies",
  creative:  "leading creative-industry cities — media, design, film and advertising",
  healthcare:"cities with the strongest medical-research, hospital and pharma pipelines",
  engineering:"top engineering and hardware manufacturing centres",
  startups:  "highest-density startup ecosystems by Startup Genome ranking",
  government:"government and public-policy capitals worldwide",
  research:  "universities and research output leaders by h-index and citation count",
};

export default function CityHubPage() {
  const { tag } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setErr(null);
      try {
        const { data } = await api.get(`/cities/hubs/${tag}`, { params: { limit: 60 } });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) {
          if (e?.response?.status === 404) navigate("/cities", { replace: true });
          else setErr(e?.response?.data?.detail || e.message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [tag, navigate]);

  const intro = TAG_INTRO[tag] || `the top student cities for ${data?.label ?? tag}`;

  return (
    <div className="min-h-screen bg-[#FBF8F3]">
      <SEOHead
        title={data ? `Top ${data.label} cities for students | WhichUniversityAdvisor` : "Loading hub…"}
        description={data ? `${data.count} student cities ranked for ${data.label}: ${intro}.` : ""}
      />

      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-10 pb-6">
        <Link to="/cities/hubs" data-testid="hub-back-link"
              className="text-xs uppercase tracking-wider text-stone-500 hover:text-[#002147] inline-flex items-center gap-1.5">
          ← Industry hubs
        </Link>
        <div className="mt-4 flex items-center gap-3">
          <Trophy className="h-6 w-6 text-[#7C3AED]" />
          <span className="text-xs uppercase tracking-wider text-[#7C3AED] font-semibold">
            {data?.kind === "industry" ? "Industry hub" : "Sector hub"}
          </span>
        </div>
        <h1 className="font-editorial text-4xl sm:text-5xl lg:text-6xl mt-2 tracking-tight text-[#002147]">
          Top <span className="text-[#7C3AED]">{data?.label ?? "…"}</span><br />
          cities for students
        </h1>
        {data && (
          <p className="mt-4 text-stone-600 max-w-2xl text-base">
            Ranked list of {data.count} student cities carrying the {data.label} tag.
            {data.methodology?.criterion && (
              <> {data.methodology.qualifying_at_7 || data.methodology.criterion}</>
            )}
          </p>
        )}
        {data?.rationale && (
          <div className="mt-4 max-w-2xl bg-violet-50 border border-violet-200 rounded-lg p-3 text-[13px] text-violet-900 leading-relaxed" data-testid="hub-rationale">
            <span className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold block mb-1">How this rank is computed</span>
            {data.rationale}
          </div>
        )}
      </section>

      {err && (
        <div className="max-w-5xl mx-auto px-6 lg:px-10 mb-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">{err}</div>
        </div>
      )}

      {data && (
        <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-16" data-testid="hub-list">
          <ol className="space-y-3">
            {data.cities.map((c, idx) => (
              <li key={c.slug}
                  className="bg-white border border-stone-200 rounded-xl p-5 hover:border-[#7C3AED] hover:shadow-md transition"
                  data-testid={`hub-card-${c.slug}`}>
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center justify-center min-w-[52px] shrink-0">
                    <span className="text-[10px] uppercase tracking-wider text-stone-400">Rank</span>
                    <span className="font-chip text-2xl text-[#002147] font-bold">#{c.rank ?? idx + 1}</span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h2 className="font-editorial text-2xl leading-tight text-[#002147]">{c.name}</h2>
                        <div className="text-xs text-stone-500 flex items-center gap-1 mt-0.5">
                          <MapPin className="h-3 w-3" /> {c.country}
                          {c.student_population ? (
                            <>
                              <span className="text-stone-300 mx-1">·</span>
                              <GraduationCap className="h-3 w-3" />
                              {(c.student_population / 1000).toFixed(0)}k students
                            </>
                          ) : null}
                        </div>
                      </div>
                      {c.safety_grade && (
                        <Badge className={`${SAFETY_COLOR[c.safety_grade] || "bg-stone-100 text-stone-700 border-stone-200"} border font-mono text-[11px]`}>
                          Safety {c.safety_grade}
                        </Badge>
                      )}
                    </div>

                    {/* Score bar — switches metric based on rank basis */}
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-stone-500 mb-1">
                        <span>
                          {data.rank_basis === "firms_present" && "Top-tier firms present"}
                          {data.rank_basis === "anchor_firms_present" && (tag === "finance" ? "G-SIBs present" : `Anchor ${data.label} companies present`)}
                          {data.rank_basis === "sector_score" && `${data.label} strength`}
                        </span>
                        <span className="font-mono text-[#7C3AED]">
                          {data.rank_basis === "firms_present"      && `${c.law_firms.length}/13`}
                          {data.rank_basis === "anchor_firms_present" && `${c.anchor_firms_present}/${data.rank_denominator}`}
                          {data.rank_basis === "sector_score"        && `${c.sector_score ?? c.raw_max}/10`}
                          {CREATIVE_HUB_TAGS.has(tag) && c.creative_provenance && (
                            <span className="ml-2 align-middle">
                              <CreativeProvenanceChip
                                provenance={c.creative_provenance}
                                testId={`hub-card-provenance-${c.slug}`}
                              />
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="h-1.5 w-full bg-stone-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#7C3AED] to-[#a855f7]"
                          style={{
                            width: `${
                              data.rank_basis === "firms_present"
                                ? Math.round((c.law_firms.length / 13) * 100)
                                : data.rank_basis === "anchor_firms_present"
                                ? Math.round((c.anchor_firms_present / data.rank_denominator) * 100)
                                : ((c.sector_score ?? c.raw_max) || 0) * 10
                            }%`,
                          }}
                        />
                      </div>
                    </div>

                    {/* Hub evidence + firms */}
                    {(tag === "law" && c.law_firms?.length > 0) ? (
                      <div className="mt-3 text-[12px] text-stone-700 leading-snug">
                        <span className="text-[10px] uppercase tracking-wider text-stone-500 mr-2 inline-flex items-center gap-1">
                          <Briefcase className="h-2.5 w-2.5" /> Firms present · {c.law_firms.length}/13
                        </span>
                        <span>{c.law_firms.slice(0, 6).join(" · ")}</span>
                        {c.law_firms.length > 6 && (
                          <span className="text-stone-400"> · +{c.law_firms.length - 6} more</span>
                        )}
                      </div>
                    ) : (data.rank_basis === "anchor_firms_present" && c.anchor_firms?.length > 0) ? (
                      <div className="mt-3 text-[12px] text-stone-700 leading-snug">
                        <span className="text-[10px] uppercase tracking-wider text-stone-500 mr-2 inline-flex items-center gap-1">
                          <Briefcase className="h-2.5 w-2.5" /> {tag === "finance" ? "G-SIBs" : "Anchors"} present · {c.anchor_firms.length}/{data.rank_denominator}
                        </span>
                        <span>{c.anchor_firms.slice(0, 6).join(" · ")}</span>
                        {c.anchor_firms.length > 6 && (
                          <span className="text-stone-400"> · +{c.anchor_firms.length - 6} more</span>
                        )}
                      </div>
                    ) : (
                      c.hubs?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1">
                          {c.hubs.slice(0, 4).map((h, i) => (
                            <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 border border-violet-200 text-violet-800">
                              {h}
                            </span>
                          ))}
                        </div>
                      )
                    )}

                    <div className="mt-4 flex items-center gap-2">
                      <Button asChild size="sm" className="bg-[#002147] text-white hover:bg-[#002147]/90"
                              data-testid={`hub-view-${c.slug}`}>
                        <Link to={`/cities/${c.slug}`}>City guide <ArrowRight className="ml-1 h-3 w-3" /></Link>
                      </Button>
                      <Button asChild size="sm" variant="outline"
                              data-testid={`hub-matcher-${c.slug}`}>
                        <Link to={`/cities/matcher?sectors=${tag}`}>
                          <Sparkles className="h-3 w-3 mr-1" /> Similar cities
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ol>

          {data.count === 0 && (
            <div className="bg-white border border-stone-200 rounded-xl p-10 text-center" data-testid="hub-empty">
              <p className="text-stone-600">No cities yet carry the <strong>{data.label}</strong> tag.</p>
            </div>
          )}
        </section>
      )}

      {/* Methodology footer */}
      {data && (
        <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-16">
          <div className="bg-white/70 border border-stone-200 rounded-lg p-5 text-xs text-stone-600 leading-relaxed space-y-3" data-testid="hub-method">
            <div className="font-semibold text-stone-700 flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" /> How this list is built
            </div>
            {data.methodology?.criterion && (
              <div>
                <div className="uppercase tracking-wider text-[10px] text-stone-500 mb-1">Signal we measure</div>
                <p>{data.methodology.criterion}</p>
              </div>
            )}
            {data.methodology?.qualifying_at_7 && (
              <div>
                <div className="uppercase tracking-wider text-[10px] text-stone-500 mb-1">What a 7+/10 city looks like</div>
                <p>{data.methodology.qualifying_at_7}</p>
              </div>
            )}
            <div>
              <div className="uppercase tracking-wider text-[10px] text-stone-500 mb-1">Cutoff</div>
              <p>
                A city carries the <strong>{data.label}</strong> tag when its raw signal is ≥ <strong>7/10</strong>. Threshold 7 is a global cutoff shared across every sector — 5–6 would dilute the ranking with hundreds of "some activity" cities; 8+ would empty out smaller niches like EdTech and AgriTech to fewer than five. So the list length ({data.count} here) reflects each sector's real-world footprint.
              </p>
            </div>
            {data.methodology?.caveats && (
              <div>
                <div className="uppercase tracking-wider text-[10px] text-stone-500 mb-1">Known limits</div>
                <p>{data.methodology.caveats}</p>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
